import React, { useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import { JiraIcon, ConfluenceIcon } from '@atlaskit/logo';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss, Text } from '@atlaskit/primitives';
import { useQuery } from '../query-context';
import { AdvancedSearchFooterLink, ReturnIconWithModifier } from './advanced-search-footer';
import { messages } from '../messages';
import { getDefaultAtlasBaseURL } from '../products/townsquare/url-generators';

import { GoogleDriveIcon } from './icons/google-icon';
import { SlackIcon } from './icons/slack-icon';
import { getTrigger, onAdvancedSearchSelected, useAnalytics } from '../../common/analytics';
import { useProductContext } from '../product-router';

import { AdvancedSearchLink } from '../../common/advanced-search-link';

const ModifierIcon = () => <ReturnIconWithModifier showModifierKey />;
const ReturnIcon = () => <ReturnIconWithModifier />;

const containerStyles = xcss({
	display: 'flex',
});

const productIconStyles = xcss({
	position: 'relative',
	display: 'flex',
	alignItems: 'center',
	width: '78px',
});

const moreButtonStyles = xcss({
	backgroundColor: 'color.background.neutral',
	borderRadius: 'radius.small',
	paddingInline: 'space.075',
	marginRight: 'space.050',
	height: '20px',
	display: 'flex',
	alignItems: 'center',
});

const absoluteStyles = xcss({
	position: 'absolute',
});

const borderStyles = xcss({
	backgroundColor: 'elevation.surface.overlay',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	height: '22px',
	width: '22px',
	borderRadius: 'radius.medium',
});

const jiraIconOverlapStyles = xcss({
	zIndex: 'dialog',
	transform: 'translate(15px, 0)',
});

const confluenceIconOverlapStyles = xcss({
	zIndex: 'navigation',
	transform: 'translate(30px, 0)',
});

const googleDriveIconOverlapStyles = xcss({
	zIndex: 'card',
	transform: 'translate(45px, 0)',
});

const slackIconOverlapStyles = xcss({
	transform: 'translate(60px, 0)',
});

const IconComponent = ({ showModifierIcon }: { showModifierIcon?: boolean }) => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={containerStyles}>
			<Box xcss={productIconStyles}>
				<Box xcss={[absoluteStyles, jiraIconOverlapStyles, borderStyles]}>
					<JiraIcon size="xsmall" shouldUseNewLogoDesign />
				</Box>
				<Box xcss={[absoluteStyles, confluenceIconOverlapStyles, borderStyles]}>
					<ConfluenceIcon size="xsmall" shouldUseNewLogoDesign />
				</Box>
				<Box xcss={[absoluteStyles, googleDriveIconOverlapStyles]}>
					<GoogleDriveIcon />
				</Box>
				<Box xcss={[absoluteStyles, slackIconOverlapStyles]}>
					<SlackIcon />
				</Box>
			</Box>
			<Box xcss={moreButtonStyles}>
				<Text size="small" align="center" color="color.text.subtle">
					{formatMessage(messages.more)}
				</Text>
			</Box>
			{showModifierIcon ? <ModifierIcon /> : <ReturnIcon />}
		</Box>
	);
};

export const getAtlassianHomeSearchUrl = (
	orgId: string,
	cloudId: string,
	query?: string,
	primaryProduct?: string,
) => {
	const url = new URL(`${getDefaultAtlasBaseURL(true)}/o/${orgId}/search`);

	url.searchParams.set('cloudId', cloudId);

	if (primaryProduct) {
		url.searchParams.set('referrer', primaryProduct);
	}

	if (query) {
		url.searchParams.set('text', query);
	}

	return url.toString();
};

export const AtlassianHomeSearchLink = ({
	orgId,
	cloudId,
	showModifierIcon,
	actionSubjectId,
}: {
	orgId: string;
	cloudId: string;
	showModifierIcon?: boolean;
	actionSubjectId?: string;
}) => {
	const { formatMessage } = useIntl();
	const { query, isLoading } = useQuery();
	const { fireAnalyticsEvent } = useAnalytics();
	const { getActiveProduct, getPrimaryProduct } = useProductContext();
	const primaryProduct = getPrimaryProduct();
	const activeProduct = getActiveProduct();
	const href = getAtlassianHomeSearchUrl(orgId, cloudId, query, primaryProduct?.id);

	const productName = activeProduct?.title;
	const defaultActionSubjectId = `${productName}AdvancedSearchLink`;

	const onAdvancedSearchClicked = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			fireAnalyticsEvent(
				onAdvancedSearchSelected({
					trigger: getTrigger(e),
					actionSubjectId: actionSubjectId || defaultActionSubjectId,
					isLoading,
					newTab: true,
					isRovoCrossProductSearchLink: true,
				}),
			);
		},
		[fireAnalyticsEvent, isLoading, actionSubjectId, defaultActionSubjectId],
	);

	return (
		<AdvancedSearchFooterLink
			href={href}
			onClick={onAdvancedSearchClicked}
			linkComponent={(props) => (
				<AdvancedSearchLink
					{...props}
					isKeyboardHighlighted={!!props.isKeyboardHighlighted}
					href={href}
					target="_blank"
				/>
			)}
			contentLabel={formatMessage(messages.extensible_advanced_search_cross_product_footer)}
			returnKeyIconComponent={() => <IconComponent showModifierIcon={showModifierIcon} />}
		/>
	);
};
